/**
 * advance-environment-effects.ts — v4M13.4b per-tick environment-
 * hazard application pass.
 *
 * For every live in-system ship, walks the hazardous environments in
 * its system and applies any whose radius contains the ship's centre:
 *   - **Black hole** (`destroyOnEntry`): set `currentHull = 0` and
 *     despawn at end of pass. Highest priority — short-circuits other
 *     effects on the same ship.
 *   - **Ion cloud** (`zeroShieldsInside`): force `currentShields` to
 *     0 (regen is suppressed elsewhere by the same flag in v4M13.4c
 *     via `advanceShipShieldRegen`).
 *   - **Ion cloud** (`breakCloakOnEntry`): set `cloaked = false`,
 *     clear `constructionOrder` + `bombardOrder` (mirrors
 *     `applyToggleCloak`'s abort-side-effects). Applied every tick
 *     the ship is inside (effectively per-entry since the ship can't
 *     re-cloak inside an ion cloud — next tick it'd be broken again).
 *   - **Radiation belt** (`hullDamagePerTick`): subtract the constant
 *     from `currentHull`. If the ship dies, despawn at end of pass.
 *
 * Stacking per `TERRAIN_FEATURES.md §4`: effects stack independently
 * (one ship can be in nebula + radiation belt + ion cloud simultaneously
 * and take all effects), but order matters slightly for shield/cloak
 * interactions:
 *   1. destroyOnEntry FIRST (short-circuits further work on the ship)
 *   2. zeroShieldsInside before damage (so combat next tick sees
 *      zero shields, not damaged shields)
 *   3. cloak break (independent state mutation)
 *   4. hullDamagePerTick last (so radiation in an ion cloud → shields
 *      stripped first AND hull damage applied; both happen)
 *
 * **Wiring:** runs in `advanceTick` BEFORE `advanceDetection` so that
 * v4M13.4c's nebula invisibility / asteroid-field LoS-broken effects
 * influence the same-tick scanner output, and BEFORE `advanceCombat`
 * so freshly-zeroed shields are visible to combat dispatch.
 *
 * **Out of scope at v4M13.4b:**
 * - Movement penalty (asteroid-field `movementSpeedMultiplier`) —
 *   needs integration with `advanceShipMovement` velocity calculation;
 *   deferred to v4M13.4c.
 * - LoS / invisibility (nebula + asteroid-field) — detection-pipeline
 *   integration deferred to v4M13.4c.
 * - Alert-feed `BlackHoleDeath` / `RadiationDeath` events — deferred
 *   to v4M13.4d (event types don't exist yet).
 * - AI hazard-awareness — pathfinding + combat picker integration
 *   deferred to v4M13.4d.
 */

import { despawnShip } from './advance-combat';
import {
  effectiveEnvironmentEffect,
  kindHasGameplayEffect,
} from './balance-environment';
import type {
  EnvironmentEntity,
  GameState,
  ShipInstance,
  ShipInstanceId,
  SystemId,
} from '../state/types/index';

/**
 * Per-tick environment-hazard application. See module header for the
 * full mechanic. Mutates ship state in place; despawns ships killed
 * by black-hole entry or radiation accumulation.
 */
export function advanceEnvironmentEffects(state: GameState): void {
  // Bucket hazardous environments by system once per tick so the
  // ship loop below is O(ships) + O(envs-in-ships'-systems) instead
  // of O(ships × all-envs). Exotic gas clouds + any other
  // gameplay-effect-free kind are filtered out via
  // `kindHasGameplayEffect` — no point including them in the scan.
  const envsBySystem = new Map<SystemId, EnvironmentEntity[]>();
  for (const env of state.environment.values()) {
    if (!kindHasGameplayEffect(env.kind)) continue;
    let bucket = envsBySystem.get(env.systemId);
    if (!bucket) {
      bucket = [];
      envsBySystem.set(env.systemId, bucket);
    }
    bucket.push(env);
  }
  if (envsBySystem.size === 0) return;

  const deadIds: ShipInstanceId[] = [];

  for (const ship of state.ships.values()) {
    // Mid-jump ships have systemId === null and are unaffected by
    // any in-system environment effect (they're in hyperspace).
    if (ship.systemId === null) continue;
    // Already-dead ships (e.g. died earlier this tick via combat
    // pre-pass) skip — they'll be despawned by their original
    // killer's path.
    if (ship.currentHull <= 0) continue;

    const envs = envsBySystem.get(ship.systemId);
    if (!envs) continue;

    for (const env of envs) {
      if (!shipIsInside(ship, env)) continue;
      const effect = effectiveEnvironmentEffect(env);

      // Destroy on entry (black hole) wins over everything else.
      // No partial state — ship just vanishes from the void.
      if (effect.destroyOnEntry) {
        ship.currentHull = 0;
        deadIds.push(ship.id);
        break;
      }

      // Shield + cloak strip (ion cloud). Per-tick application so
      // re-cloaking while still inside immediately gets broken on
      // the next tick. Cloak-break side-effects mirror
      // `applyToggleCloak` in `commands.ts`.
      if (effect.zeroShieldsInside) {
        ship.currentShields = 0;
      }
      if (effect.breakCloakOnEntry && ship.cloaked) {
        ship.cloaked = false;
        ship.constructionOrder = null;
        ship.bombardOrder = null;
      }

      // Continuous radiation damage. Bypasses shields entirely
      // (PLANETS.md §4.7 + TERRAIN_FEATURES.md §1.6 — "direct hull
      // damage"). Accumulates across overlapping radiation belts
      // because effects stack independently per §4 — a ship in two
      // overlapping belts loses 2 × hullDamagePerTick hull per tick.
      if (
        effect.hullDamagePerTick !== undefined &&
        effect.hullDamagePerTick > 0
      ) {
        ship.currentHull -= effect.hullDamagePerTick;
        if (ship.currentHull <= 0) {
          ship.currentHull = 0;
          deadIds.push(ship.id);
          break;
        }
      }
    }
  }

  for (const id of deadIds) {
    despawnShip(state, id);
  }
}

/**
 * Squared-distance check vs squared-radius: ship centre `(sx, sy)`
 * inside the environment's footprint when `(ship.sx - env.sx)² +
 * (ship.sy - env.sy)² <= env.radius²`. No square-root needed.
 */
function shipIsInside(ship: ShipInstance, env: EnvironmentEntity): boolean {
  const dx = ship.sx - env.sx;
  const dy = ship.sy - env.sy;
  return dx * dx + dy * dy <= env.radius * env.radius;
}
